import clsx from "clsx";
import React from "react";
import {
  Box,
  BoxProps,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { AppLogo } from "../app/AppLogo";
import { ConsentBanner } from "../app/ConsentBanner";
import {
  WorkspaceInfoDto,
  UnitSystem,
} from "@growth-machine-llc/stridist-api-client";
import { useMutation } from "@tanstack/react-query";
import FbUsersService from "../../services/FbUsersService";
import { DefaultLoader } from "../loading/DefaultLoader";
import { useGenericErrorHandler } from "../../hooks/useGenericErrorHandler";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import useAuth from "../../hooks/auth/useAuth";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { validatePassword } from "../../utils/password";
import { ClientAcceptInviteInvalid } from "./ClientAcceptInviteInvalid";
import { AcceptClientInviteInvalidReason } from "../../constants";
import PasswordField from "../fields/PasswordField";
import SignUpService from "../../services/SignUpService";
import TeamService from "../../services/TeamService";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100vw",
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(10, 3),
    backgroundColor: theme.palette.background.default,
  },

  title: {
    fontSize: 24,
    fontWeight: 600,
    lineHeight: "29px",
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1.5),
  },

  subtitle: {
    fontSize: 16,
    fontWeight: 500,
    lineHeight: "20px",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(3.5),
  },

  logo: {
    marginBottom: theme.spacing(4),
    ...(theme.palette.mode === "dark" && {
      filter: "invert(1)",
    }),
  },
  field: {
    marginTop: theme.spacing(2),
    width: "100%",
    maxWidth: 480,
  },
}));

export interface CoachAcceptInviteScreenProps extends BoxProps {
  workspace: WorkspaceInfoDto;
  email: string;
  code: string;
}

export function CoachAcceptInviteScreen(props: CoachAcceptInviteScreenProps) {
  const { className, workspace, email, code, ...other } = props;
  const navigate = useNavigate();
  const s = useStyles();
  const [password, setPassword] = React.useState("");
  const [passwordError, setPasswordError] = React.useState("");
  const [units, setUnits] = React.useState<UnitSystem>(UnitSystem.US);
  const [isVerified, setIsVerified] = React.useState<null | boolean>(null);
  const onError = useGenericErrorHandler({
    setFieldError: (field, message) => {
      field === "password" && setPasswordError(message);
    },
  });
  const user = useCurrentUser();

  const { loginWithToken } = useAuth();
  const { mutate: verifyEmail } = useMutation({
    mutationKey: ["verify-coach-email"],
    mutationFn: FbUsersService.verifyUserEmail,
    onSuccess: () => setIsVerified(true),
    onError: () => setIsVerified(false),
  });

  const { mutate: completeSignup, isPending: completingSignup } = useMutation({
    mutationKey: ["complete-coach-sign-up"],
    mutationFn: TeamService.joinTeam,
    onError,
  });

  React.useEffect(() => {
    verifyEmail({ email, oobCode: code });
  }, [email, code, verifyEmail]);

  const handlePasswordChange = React.useCallback(
    ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
      !value.length && setPasswordError("");
      setPassword(value);
    },
    [],
  );

  const handleSubmit = React.useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!isVerified) return;

      const pwdError = validatePassword(password);
      if (pwdError) {
        setPasswordError(pwdError);
        return;
      }

      completeSignup(
        {
          email,
          password,
          workspaceSlug: workspace.slug,
          oobCode: code,
        },
        {
          onSuccess: async (response) => {
            const accessToken = response?.accessToken;
            await loginWithToken(accessToken);
            navigate("/");
          },
        },
      );
    },
    [
      isVerified,
      email,
      password,
      workspace.slug,
      code,
      completeSignup,
      loginWithToken,
      navigate,
    ],
  );

  if (user) {
    return (
      <ClientAcceptInviteInvalid
        reason={AcceptClientInviteInvalidReason.ANOTHER_USER_LOGGED}
      />
    );
  }

  if (!workspace) {
    return (
      <ClientAcceptInviteInvalid
        reason={AcceptClientInviteInvalidReason.INVITATION_REVOKED}
      />
    );
  }

  if (isVerified === false) {
    return (
      <ClientAcceptInviteInvalid
        reason={AcceptClientInviteInvalidReason.LINK_EXPIRED}
        title={"Link Expired"}
      />
    );
  }

  if (isVerified === null) {
    return <DefaultLoader fillScreen />;
  }

  const loading = completingSignup || !isVerified;
  const passwordErrors = validatePassword(password);
  const passwordValid = !passwordErrors;

  return (
    <Box className={clsx(s.root, className)} {...other}>
      <AppLogo className={s.logo} />

      <Typography className={s.title} variant="h1">
        Welcome to the {workspace.name} team!
      </Typography>

      <Typography className={s.subtitle} variant="h5">
        Please enter your email and password to join the team.
      </Typography>

      <Box
        component={"form"}
        onSubmit={handleSubmit}
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <TextField
          className={s.field}
          variant="filled"
          name="email"
          type="email"
          label="Email address"
          placeholder="Email address"
          value={email}
          disabled={true}
        />

        <PasswordField
          className={s.field}
          variant="filled"
          name="password"
          label="Password"
          placeholder="Password"
          value={password}
          sx={{ minWidth: 480, maxWidth: "100%", marginBottom: 2 }}
          onChange={handlePasswordChange}
          error={Boolean(passwordError)}
          helperText={
            passwordError ||
            "Your password should be at least 8 characters long."
          }
          fullWidth
        />

        <Box className={s.field}>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={Boolean(!passwordValid || loading)}
          >
            Sign Up Free
          </Button>
        </Box>
      </Box>
      <ConsentBanner />
    </Box>
  );
}
